import React from "react";
import { Image } from "react-bootstrap";

import Cover3 from "../../../assets/images/articles/history/cover3.png";
import Citations from "../Citations";
import { parseCitations } from "../utils/parse-citations";

const citations = [
  { id: 1, text: "Bacon, F. (1620). Novum Organum [New organon]. In J. Spedding, R. L. Ellis, & D. D. Heath (Eds.), The Works of Francis Bacon (Vol. 4, 1858 translation)." },
  { id: 2, text: "Andersen, H., & Hepburn, B. (2015). Scientific method. In E. N. Zalta (Ed.), The Stanford Encyclopedia of Philosophy. Stanford University. https://plato.stanford.edu/entries/scientific-method/" },
  { id: 3, text: "Allen, M., & Mehler, D. M. A. (2020). Robert Boyle on the importance of reporting and replicating experiments. Journal of the Royal Society of Medicine, 113(3), 107–111. https://doi.org/10.1177/0141076820902625" },
  { id: 4, text: "Shapin, S. (1984). Pump and circumstance: Robert Boyle's literary technology. Social Studies of Science, 14(4), 481–520. https://doi.org/10.1177/030631284014004001" },
  { id: 5, text: "Panayotova, P. (2023). Inventing the language of things: The emergence of scientific reporting in seventeenth-century England. Annals of Science, 80(4), 481–510. https://doi.org/10.1080/00033790.2023.2235364" }
];

export default function HistoryOfScientificMethodPart3() {
  return (
    <article
      className="px-3 px-md-4 py-4"
      style={{
        fontSize: "1.05rem",
        lineHeight: 1.8,
        color: "#212529",
        maxWidth: "900px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <header className="text-center mb-5">
        <h2 className="fw-bold mb-3">
          The History of the Scientific Method: Chapter 3
        </h2>
        <p className="text-muted">
          The Scientific Revolution: Bacon, Boyle & the Rise of Scientific Societies
        </p>

        <Image
          src={Cover3}
          alt="scientific revolution"
          fluid
          rounded
          className="my-4 shadow-sm"
        />
      </header>

      {/* Intro */}
      <section>
        <p>
          {parseCitations(`
            The Scientific Revolution of the 16th and 17th centuries marked a turning point in how scientific knowledge was generated, evaluated, and shared. Building on ideas transmitted from the Islamic Golden Age through Latin translations, European natural philosophers. formalizatized experimentation, elevated reproducibility as a scientific standard, and created institutions designed for collaborative knowledge building.`)}
        </p>

        <p>
          {parseCitations(`
            This transformation established practices that remain at the core of modern science, and at the heart of today's debates about reproducibility.`)}
        </p>
      </section>

      <h2 className="fw-bold mt-5 mb-3">Francis Bacon: A Framework for Inquiry</h2>

      <section>
        <p>
          {parseCitations(`
            By the early 1600s, natural philosophers faced a problem still recognizable today: humans see what they wanted to see. Sir Francis Bacon (1561 - 1626), often called the "father of the scientific method," believed that science needed a method – a disciplined way to move from observation to understanding – to counteract these biases.`)}
        </p>

        <p>
          {parseCitations(`
            In his 1620 work Novum Organum (New Organon) [1][2], Bacon argued that science must avoid grand conclusions drawn from limited observations. Instead, knowledge should come from carefully gathered data (“histories”), arranged in a systematic ways so underlying patterns could be revealed.`)}
        </p>

        <p>
          {parseCitations(`
            For example, when studying heat, Bacon created comparative tables listing cases where heat is present ("Table of Essence and Presence") and nearly identical cases where it was absent ("Table of Deviation, or of Absence in Proximity"). By analyzing contrasts across such tables, he believed that investigators could begin to infer causal principles.`)}
        </p>

        <p>
          {parseCitations(`
            Bacon also warned that human reasoning is riddled with what he called "Idols". These systemic biases – human cognition, personal experience, imprecise language, and devotion to dogma – threaten objectivity. Identifying and countering these biases became part of his vision for scientific progress.`)}
        </p>

        <p>
          {parseCitations(`
            Yet Bacon was not only a method builder; he was an institutional visionary. In his fictional novel New Atlantis, he reimagined research institutions where investigators collaborated to collect, refine, and interpret observations. This anticipation of scientific societies would soon materialize.`)}
        </p>
      </section>

      <h2 className="fw-bold mt-5 mb-3">Robert Boyle: Reproducibility Becomes a Rule</h2>

      <section>
        <p>
          {parseCitations(`
            Robert Boyle (1627 - 1691) transformed Bacon’s ideas into laboratory discipline. made perhaps the most critical contribution to scientific practice: establishing reproducibility as a cornerstone of scientific knowledge [3].`)}
        </p>
      </section>

      <h3 className="fw-semibold mt-4 mb-3">The Legal Analogy for Replication</h3>

      <section>
        <p>
          {parseCitations(`
            Through his air pump experiments in the 1660s, Boyle made a revolutionary claim: a scientific result means little unless others can reproduce it.`)}
        </p>

        <p>
          {parseCitations(`
            Boyle compared scientific evidence to courtroom testimony. One witness is not enough to convict, and scientific claims, he argued, require multiple independent confirmations. This seemingly simple idea of knowledge validated through replication, became one of the defining standards of modern medicine.`)}
        </p>
      </section>

      <h3 className="fw-semibold mt-4 mb-3">Three Expectations for Reliable Experiments</h3>

      <section>
        <p>From this perspective, Boyle developed three core principles for credible experimental work [3]:</p>
        <p>Repeat the experiment under different conditions</p>
        <p>Perform it many times</p>
        <p>Include independent witnesses</p>

        <p>
          {parseCitations(`
            These expectations now seem obvious, but Boyle was among the first to articulate them explicitly.`)}
        </p>
      </section>

      <h3 className="fw-semibold mt-4 mb-3">Documentation as a Scientific Tool</h3>

      <section>
        <p>
          {parseCitations(`
            Boyle understood that replication requires clear, detailed reporting. He described his apparatus, procedures, materials, and even sources of error so thoroughly that others could attempt the same experiments. Historian Steven Shapin later called this approach “literary technology”: writing designed to let distant readers function as “virtual witnesses” to experimental work.`)}
        </p>

        <p>
          {parseCitations(`
            Boyle also addressed a topic that still challenges scientists: failed experiments. In his essay On the Unsuccessfulness of Experiments, he encouraged openness about difficulties, recognizing that variability, in materials, environment, or techniques, is intrinsic to experimental practice.`)}
        </p>

        <p>
          {parseCitations(`
            Through this combination of methodological clarity and candor, Boyle helped cement replication and transparent reporting as core scientific values.`)}
        </p>
      </section>

      <h2 className="fw-bold mt-5 mb-3">Scientific Societies: Making Knowledge Public</h2>

      <section>
        <p>
          {parseCitations(`
            As experimentation flourished, new organizations emerged to support collective inquiry. These scientific societies became the first institutions explicitly designed to create, evaluate, and disseminate scientific knowledge.`)}
        </p>
      </section>

      <h3 className="fw-semibold mt-4 mb-3">The Royal Society of London (1660)</h3>

      <section>
        <p>
          {parseCitations(`
            The Royal Society adopted Nullius in verba ("Take nobody's word for it"), as its motto, signaling a commitment to evidence over authority [4].`)}
        </p>

        <p>
          {parseCitations(`
            The Society hosted public demonstrations of experiments, discussions, and debates; encouraged collaborative knowledge building; and established early systems for scientific publication. This moved science from private laboratories to a shared arena where claims could be tested, challenged, and refined[4].`)}
        </p>
      </section>

      <h3 className="fw-semibold mt-4 mb-3">The Académie Royale des Sciences (1666)</h3>

      <section>
        <p>
          {parseCitations(`
            Founded shortly afterward in Paris, the Académie provided state-supported infrastructure for systematic inquiry. It helped standardized scientific communication and reinforced the idea that scientific work is a collaborative, not solitary, enterprise.`)}
        </p>

        <p>
          {parseCitations(`
            Together, these institutions marked a shift from private investigation to public science that could be scrutinized, recorded, and repeated.`)}
        </p>
      </section>

      <h2 className="fw-bold mt-5 mb-3">Robert Hooke and the Art of Scientific Description</h2>

      <section>
        <p>
          {parseCitations(`
            As the Royal Society’s curator of experiments, Robert Hooke (1635 - 1703) set new standards for experimental reporting[5]. His 1665 masterpiece Micrographia, paired meticulous procedures with extraordinary illustrations of microscopic observations. Hooke’s documentation provided explicit descriptions of magnification levels, specimen preparation, and observational conditions.`)}
        </p>

        <p>
          {parseCitations(`
            The resulting book was both rigorous and captivating. Its success proved something crucial: detailed scientific documentation can be both precise and engaging, a lesson modern scientific communication still struggles with.`)}
        </p>
      </section>

      <h2 className="fw-bold mt-5 mb-3">Key Takeaways</h2>

      <section>
        <p>The Scientific Revolution solidified a set of principles that continue to shape research culture today:</p>
        <p>- Systematic methodology (Bacon): Systematic observation and inductive reasoning</p>
        <p>- Reproducibility (Boyle): Independent verification as the standard of truth</p>
        <p>- Detailed documentation (Boyle, Hooke): enabling others to replicate work</p>
        <p>- Institutional support (Royal Society, Académie): Communal oversight and shared standards</p>
        <p>- Transparent communication: the foundation for modern methods sections</p>

        <p>
          These principles helped transform science into a disciplined, collaborative, and reproducible enterprise.
        </p>

        <p>
          In Part 4, we'll explore how these principles were formalized into laboratory notebooks you might recognize from your own studies or labs – and how science became a profession.
        </p>
      </section>

      {/* References */}
      <h3 className="fw-semibold mt-4">References</h3>
      <Citations items={citations} />
    </article>
  );
}
